const MINUTES_IN_HOUR = 60
const HOURS_IN_DAY = 24
const MAX_MINUTE = 59

function isDigits(value: string): boolean {
  return value.length > 0 && /^\d+$/.test(value)
}

function splitHoursAndMinutes(numeric: string): [number, number] | null {
  if (numeric.includes(':')) {
    const parts = numeric.split(':')
    if (parts.length !== 2 || !isDigits(parts[0]) || !isDigits(parts[1])) {
      return null
    }
    return [Number(parts[0]), Number(parts[1])]
  }

  if (!isDigits(numeric)) {
    return null
  }

  if (numeric.length <= 2) {
    return [Number(numeric), 0]
  }
  if (numeric.length === 3) {
    return [Number(numeric.slice(0, 1)), Number(numeric.slice(1))]
  }
  if (numeric.length === 4) {
    return [Number(numeric.slice(0, 2)), Number(numeric.slice(2))]
  }
  return null
}

/**
 * Parse free-typed time text into minutes-from-midnight, or null when the text
 * can't be read as a valid time. Tolerant of shorthand: `6`, `6am`, `6:00 PM`,
 * `18:00`, `1800`, with or without spaces, meridiem, and dots. Input without a
 * meridiem is read as a literal 24-hour value (`18` → 18:00) in every format.
 */
export function parseTimeInput(text: string): number | null {
  let normalized = text
    .trim()
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\s+/g, '')
  if (!normalized) {
    return null
  }

  let meridiem: 'am' | 'pm' | null = null
  const meridiemMatch = normalized.match(/(am|pm|a|p)$/)
  if (meridiemMatch) {
    meridiem = meridiemMatch[1][0] === 'a' ? 'am' : 'pm'
    normalized = normalized.slice(0, meridiemMatch.index)
  }

  const parsed = splitHoursAndMinutes(normalized)
  if (!parsed) {
    return null
  }

  let [hours] = parsed
  const [, minutes] = parsed
  if (minutes > MAX_MINUTE) {
    return null
  }

  if (meridiem) {
    if (hours < 1 || hours > 12) {
      return null
    }
    if (meridiem === 'pm') {
      hours = hours === 12 ? 12 : hours + 12
    } else {
      hours = hours === 12 ? 0 : hours
    }
  } else if (hours < 0 || hours >= HOURS_IN_DAY) {
    return null
  }

  return hours * MINUTES_IN_HOUR + minutes
}
